import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { CheckBox, ResponsiveContext, Spinner } from 'grommet';
import { observer } from 'mobx-react-lite';
import {
  ApiError,
  ClientReportingPeriodService,
  TListClientReportingPeriodsRequest,
  TUpdateMeteredEquipmentUsageRequest,
  TUpdateUnmeteredEquipmentUsageRequest,
} from '/src/api';
import {
  AddButton,
  Box,
  Card,
  CardBody,
  CardHeader,
  EquipmentUsageIcon,
  FormCardSection,
  Input,
  ReportingPeriodDropdown,
  Select,
  Text,
  UsagesDataTable,
} from '/src/components';
import { useEquipmentStore, useEquipmentUsageStore, useGlobalStore, useUserStore } from '/src/context';
import { errorMessages } from '/src/lib/errors';
import { ClientReportingPeriod, Equipment, EquipmentUsage, TEquipmentUsageId, isFciCategory } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import {
  DataTableCell,
  EquipmentUsageDataForm,
  MeteredEquipmentUsageDataForm,
  ReportingPeriodType,
  UnmeteredEquipmentUsageDataForm,
} from '/src/lib/types';
import { filterSelectOptions, getIsMobile, getIsUsageEditable, getPeriodFromDateString, pxToRem } from '/src/utils';

export type EquipmentUsagesListProps = {
  equipment: Equipment;
  dataTour?: string;
};

export const EquipmentUsagesList: React.FC<EquipmentUsagesListProps> = observer((props) => {
  const { equipment, dataTour } = props;

  /** Context **/
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const equipmentUsageStore = useEquipmentUsageStore();
  const equipmentStore = useEquipmentStore();
  const screenSize = useContext(ResponsiveContext);
  const { t } = useTranslation();

  /** Refs **/
  const defaultFormValues = useRef<EquipmentUsageDataForm>(
    equipment.is_metered
      ? {
          fuel_pathway_id: undefined,
          is_book_and_claim_applied: null,
          total_kwh: null,
          percent_uptime: '95',
          max_site_deliverability_kw: '',
        }
      : {
          fuel_pathway_id: undefined,
          is_book_and_claim_applied: null,
          is_paused: false,
          battery_capacity_rating_ah: '',
          voltage: '',
          charge_cycles_per_shift: '0',
          shifts_per_day: '0',
          work_days_per_quarter: '0',
          percent_charger_efficiency_rating: '0',
          percent_charge_return_factor: '0',
          percent_depth_of_discharge: '0',
          percent_uptime: '95',
          max_site_deliverability_kw: '',
        }
  );

  /** State **/
  const [isFetchingUsages, setIsFetchingUsages] = useState(true);
  const [isFetchingPeriods, setIsFetchingPeriods] = useState(true);
  const [isFetchingPathways, setIsFetchingPathways] = useState(true);
  const [isUpdatingCurrent, setIsUpdatingCurrent] = useState(false);
  const [reportingPeriods, setReportingPeriods] = useState<ClientReportingPeriod[]>();
  const [selectedReportingPeriod, setSelectedReportingPeriod] = useState<ClientReportingPeriod>();

  const [oldWorkDaysValue, setOldWorkDaysValue] = useState<string | number>();
  const [currentUsage, setCurrentUsage] = useState<EquipmentUsage>();
  const [formValues, setFormValues] = useState<EquipmentUsageDataForm>(defaultFormValues.current);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  /** Computed **/
  const isMobile = getIsMobile(screenSize);
  const isLoading = isFetchingPeriods || isFetchingUsages || isFetchingPathways;
  const selectedIndex = reportingPeriods?.findIndex((p) => p.id === selectedReportingPeriod?.id) ?? -1;
  const previousPeriod = selectedIndex > -1 ? reportingPeriods?.[selectedIndex + 1] : undefined;
  const nextPeriod = selectedIndex > -1 ? reportingPeriods?.[selectedIndex - 1] : undefined;
  const isMetered = equipment.is_metered;
  const isCanada = equipment?.reporting_period_type_id === ReportingPeriodType.Yearly;
  const isQ2_2025 = selectedReportingPeriod?.start_reporting_quarter === '2025-04-01';
  const showFciFields = isFciCategory(
    equipmentStore.formValues.equipment_category_id ?? equipment?.equipment_type?.equipment_category?.id
  );

  const isEditable =
    userStore.isExternalUser && isQ2_2025 ? false : getIsUsageEditable(userStore.isExternalUser, currentUsage);
  const disabledMessage = currentUsage
    ? userStore.isExternalUser && (selectedReportingPeriod?.is_client_locked || isQ2_2025)
      ? t('equipment:periodHasBeenLockedAndCannotBeEdited')
      : t('equipment:periodHasBeenFinalizedAndCanNoLongerBeEdited')
    : '';

  /** Methods **/

  /**
   * Fetch Reporting Periods
   */
  const fetchReportingPeriods = async (equipment: Equipment) => {
    try {
      setIsFetchingPeriods(true);

      const request: TListClientReportingPeriodsRequest = {
        client_id: equipment.client_id,
        program_id: equipment.program_id,
        limit: 1000,
        reporting_period_type_id: equipment.reporting_period_type_id,
        start_reporting_quarter: equipment.first_active_reporting_quarter,
      };

      if (equipment.last_active_reporting_quarter) {
        request.end_reporting_quarter = equipment.last_active_reporting_quarter;
      }

      const { data } = await ClientReportingPeriodService.list(request);

      const periodData = data
        .reverse()
        .sort((a, b) =>
          a.start_reporting_quarter > b.start_reporting_quarter
            ? -1
            : a.start_reporting_quarter === b.start_reporting_quarter
              ? 0
              : 1
        );

      setReportingPeriods(periodData);

      if (periodData[0]) setSelectedReportingPeriod(periodData[0]);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.listClientReportingPeriods.error);
    } finally {
      setIsFetchingPeriods(false);
    }
  };

  /**
   * Fetches page data.
   * @param equipment
   * @param reportingPeriod
   */
  const fetchPageData = async (equipment: Equipment, reportingPeriod: ClientReportingPeriod) => {
    try {
      setIsFetchingUsages(true);
      setIsFetchingPathways(true);

      const equipmentUsages = await equipmentUsageStore.listEquipmentUsages({
        equipment_id: equipment.id,
        client_program_reporting_period_id: reportingPeriod.id,
      });

      const usage = equipmentUsages?.find(
        (u) =>
          u.start_reporting_quarter === reportingPeriod.start_reporting_quarter &&
          u.end_reporting_quarter === reportingPeriod.end_reporting_quarter
      );
      setCurrentUsage(usage);

      if (usage) {
        const updatedFormValues: EquipmentUsageDataForm = isMetered
          ? ({
              is_book_and_claim_applied:
                usage.is_book_and_claim_applied !== undefined
                  ? usage.is_book_and_claim_applied
                  : defaultFormValues.current.is_book_and_claim_applied,
              fuel_pathway_id: usage.fuel_pathway_id,
              total_kwh: usage.total_kwh ?? null,
              percent_uptime:
                usage.percent_uptime !== undefined ? usage.percent_uptime : defaultFormValues.current.percent_uptime,
              max_site_deliverability_kw:
                usage.max_site_deliverability_kw ?? defaultFormValues.current.max_site_deliverability_kw,
            } as MeteredEquipmentUsageDataForm)
          : ({
              is_book_and_claim_applied:
                usage.is_book_and_claim_applied !== undefined
                  ? usage.is_book_and_claim_applied
                  : defaultFormValues.current.is_book_and_claim_applied,
              percent_uptime:
                usage.percent_uptime !== undefined ? usage.percent_uptime : defaultFormValues.current.percent_uptime,
              max_site_deliverability_kw:
                usage.max_site_deliverability_kw?.toString() ?? defaultFormValues.current.max_site_deliverability_kw,
              is_paused: usage.is_paused,
              fuel_pathway_id: usage.fuel_pathway_id,
              battery_capacity_rating_ah: usage.battery_capacity_rating_ah?.toString() ?? '',
              voltage: usage.voltage?.toString() ?? '',
              charge_cycles_per_shift: usage.charge_cycles_per_shift ?? '',
              shifts_per_day: usage.shifts_per_day ?? '',
              work_days_per_quarter: usage.is_paused ? '0' : (usage.work_days_per_quarter ?? '0'),
              percent_charger_efficiency_rating: usage.percent_charger_efficiency_rating?.toString() ?? '',
              percent_charge_return_factor: usage.percent_charge_return_factor?.toString() ?? '',
              percent_depth_of_discharge: usage.percent_depth_of_discharge?.toString() ?? '',
            } as UnmeteredEquipmentUsageDataForm);

        setFormValues({
          ...formValues,
          ...updatedFormValues,
        });

        if (equipment.facility?.program_id && equipment.facility?.address_region_id) {
          await equipmentUsageStore.fetchFuelPathways(
            equipment.facility.program_id,
            equipment.facility.address_region_id,
            usage.start_reporting_quarter,
            usage.end_reporting_quarter
          );
        }
      }
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.listEquipmentUsages.error);
    } finally {
      setIsFetchingUsages(false);
      setIsFetchingPathways(false);
    }
  };

  /**
   * Filter Fuel Pathways by name. Used by the Fuel Pathways dropdown search box.
   * @param nameFilter
   */
  const searchPathways = (nameFilter: string) => {
    if (nameFilter === '') equipmentUsageStore.setFuelPathwayOptions(equipmentUsageStore.defaultFuelPathwayOptions);
    else {
      const filteredPathways = filterSelectOptions<number>(nameFilter, equipmentUsageStore.defaultFuelPathwayOptions);
      equipmentUsageStore.setFuelPathwayOptions(filteredPathways);
    }
  };

  /**
   * Validate a single field value
   * @param key
   * @param value
   * @param isMetered
   */
  const validateFieldValue = (key: string, value: string | number | null, isMetered: boolean): string => {
    const validationRules = isMetered
      ? [
          { field: 'total_kwh', min: 0, max: undefined, displayName: 'Total kWh' },
          { field: 'percent_uptime', min: 0, max: undefined, displayName: 'Uptime' },
          { field: 'max_site_deliverability_kw', min: 0, max: undefined, displayName: 'Max Site Deliverability (kW)' },
        ]
      : [
          { field: 'battery_capacity_rating_ah', min: 0.001, max: undefined, displayName: 'Battery Capacity' },
          { field: 'voltage', min: 0.001, max: undefined, displayName: 'Voltage' },
          { field: 'charge_cycles_per_shift', min: 0, max: 9.99, displayName: 'Charge Cycles/Shift' },
          { field: 'shifts_per_day', min: 0, max: 9.99, displayName: 'Shifts/Day' },
          { field: 'work_days_per_quarter', min: 0, max: undefined, displayName: 'Work Days/Quarter' },
          { field: 'percent_charger_efficiency_rating', min: 0, max: undefined, displayName: 'Charger Efficiency' },
          { field: 'percent_charge_return_factor', min: 0, max: undefined, displayName: 'Charge Return Factor' },
          { field: 'percent_depth_of_discharge', min: 0, max: undefined, displayName: 'Depth of Discharge' },
          { field: 'percent_uptime', min: 0, max: undefined, displayName: 'Uptime' },
          { field: 'max_site_deliverability_kw', min: 0, max: undefined, displayName: 'Max Site Deliverability (kW)' },
        ];

    const rule = validationRules.find((r) => r.field === key);
    if (!rule) return '';

    if (value === null || value === '') {
      return '';
    }

    const numValue = parseFloat(value.toString());
    if (isNaN(numValue)) {
      return '';
    }

    if (numValue < rule.min || (rule.min > 0 && numValue === 0)) {
      return `${rule.displayName} must be ${rule.min === 0 ? '0 or greater' : `greater than ${rule.min}`}`;
    } else if (rule.max !== undefined && numValue > rule.max) {
      return `${rule.displayName} must be less than or equal to ${rule.max}`;
    }

    return '';
  };

  /**
   * Update form data
   * @param key
   * @param value
   */
  const updateFormValue = (key: string, value: string | number | boolean | null) => {
    const processedValue = key === 'total_kwh' && value === '' ? null : value;

    const updatedFormValues = {
      ...formValues,
      [key]: processedValue,
    };

    setFormValues(updatedFormValues);

    const error = validateFieldValue(key, processedValue as string | number | null, isMetered);
    setFormErrors({
      ...formErrors,
      [key]: error,
    });
  };

  /**
   * Form validation for equipment usage
   * @param formValues
   * @param isMetered
   */
  const validateFormValues = (formValues: EquipmentUsageDataForm, isMetered: boolean) => {
    const errors: Record<string, string> = {};

    const validationRules = isMetered
      ? [
          { field: 'total_kwh', min: 0, max: undefined, displayName: 'Total kWh' },
          { field: 'max_site_deliverability_kw', min: 0, max: undefined, displayName: 'Max Site Deliverability (kW)' },
          ...(showFciFields
            ? [
                {
                  field: 'percent_uptime',
                  min: 0,
                  max: undefined,
                  displayName: 'Uptime',
                },
              ]
            : []),
        ]
      : [
          { field: 'battery_capacity_rating_ah', min: 0.001, max: undefined, displayName: 'Battery Capacity' },
          { field: 'voltage', min: 0.001, max: undefined, displayName: 'Voltage' },
          { field: 'charge_cycles_per_shift', min: 0, max: 9.99, displayName: 'Charge Cycles/Shift' },
          { field: 'shifts_per_day', min: 0, max: 9.99, displayName: 'Shifts/Day' },
          { field: 'work_days_per_quarter', min: 0, max: undefined, displayName: 'Work Days/Quarter' },
          { field: 'percent_charger_efficiency_rating', min: 0, max: undefined, displayName: 'Charger Efficiency' },
          { field: 'percent_charge_return_factor', min: 0, max: undefined, displayName: 'Charge Return Factor' },
          { field: 'percent_depth_of_discharge', min: 0, max: undefined, displayName: 'Depth of Discharge' },
          { field: 'max_site_deliverability_kw', min: 0, max: undefined, displayName: 'Max Site Deliverability (kW)' },
          ...(showFciFields
            ? [
                {
                  field: 'percent_uptime',
                  min: 0,
                  max: undefined,
                  displayName: 'Uptime',
                },
              ]
            : []),
        ];

    validationRules.forEach(({ field, min, max, displayName }) => {
      const value = formValues[field] as string | null;

      if (field === 'percent_uptime' && showFciFields && (value === null || value === '')) {
        errors[field] = 'Uptime is required';
        return;
      }

      if (value === null || value === '') {
        return;
      }
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < min || (min > 0 && numValue === 0)) {
        errors[field] = `${displayName} must be ${min === 0 ? '0 or greater' : `greater than ${min}`}`;
      } else if (max !== undefined && numValue > max) {
        errors[field] = `${displayName} must be less than or equal to ${max}`;
      }
    });

    return errors;
  };

  /**
   * Update Equipment Usage
   */
  const updateEquipmentUsage = async (equipmentUsageId: TEquipmentUsageId, formValues: EquipmentUsageDataForm) => {
    try {
      const errors = validateFormValues(formValues, isMetered);

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        toast.error(Object.values(errors)[0]);
        return;
      }

      setIsUpdatingCurrent(true);

      if (isMetered) {
        /* Update Metered */
        const values = formValues as MeteredEquipmentUsageDataForm;
        const newUsage: TUpdateMeteredEquipmentUsageRequest = {
          equipment_usage_id: equipmentUsageId,
          fuel_pathway_id: typeof values.fuel_pathway_id === 'number' ? values.fuel_pathway_id : null,
          is_book_and_claim_applied: values.is_book_and_claim_applied,
          total_kwh: values.total_kwh ? parseFloat(values.total_kwh) : null,
          percent_uptime: values.percent_uptime ? parseInt(values.percent_uptime) : null,
          max_site_deliverability_kw: values.max_site_deliverability_kw
            ? parseInt(values.max_site_deliverability_kw)
            : null,
        };
        await equipmentUsageStore.updateMeteredEquipmentUsage(newUsage);
      } else {
        /* Update Unmetered */
        const values = formValues as UnmeteredEquipmentUsageDataForm;
        const newUsage: TUpdateUnmeteredEquipmentUsageRequest = {
          equipment_usage_id: equipmentUsageId,
          fuel_pathway_id: typeof values.fuel_pathway_id === 'number' ? values.fuel_pathway_id : null,
          is_book_and_claim_applied: values.is_book_and_claim_applied,
          percent_uptime: values.percent_uptime ? parseInt(values.percent_uptime) : null,
          max_site_deliverability_kw: values.max_site_deliverability_kw
            ? parseInt(values.max_site_deliverability_kw)
            : null,
          is_paused: values.is_paused as boolean,
          battery_capacity_rating_ah: parseFloat(values.battery_capacity_rating_ah?.toString() ?? '0'),
          voltage: parseFloat(values.voltage?.toString() ?? '0'),
          charge_cycles_per_shift: values.charge_cycles_per_shift
            ? parseFloat(parseFloat(values.charge_cycles_per_shift).toFixed(2))
            : 0,
          shifts_per_day: values.shifts_per_day ? parseFloat(parseFloat(values.shifts_per_day).toFixed(2)) : 0,
          work_days_per_quarter: values.is_paused
            ? 0
            : values.work_days_per_quarter
              ? parseFloat(parseFloat(values.work_days_per_quarter).toFixed(2))
              : 0,
          percent_charger_efficiency_rating: values.percent_charger_efficiency_rating
            ? parseInt(values.percent_charger_efficiency_rating)
            : 0,
          percent_charge_return_factor: values.percent_charge_return_factor
            ? parseInt(values.percent_charge_return_factor)
            : 0,
          percent_depth_of_discharge: values.percent_depth_of_discharge
            ? parseInt(values.percent_depth_of_discharge)
            : 0,
        };
        await equipmentUsageStore.updateUnmeteredEquipmentUsage(newUsage);
      }

      toast.success(toastMessages.updateEquipmentUsage.success);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.updateEquipmentUsage.error);
    } finally {
      setIsUpdatingCurrent(false);
    }
  };

  const handleCheckBox = (isChecked: boolean) => {
    if (isChecked) {
      setOldWorkDaysValue(formValues.work_days_per_quarter as string | number);
    }
    updateFormValue('is_paused', isChecked);
  };

  /** Effects **/
  useEffect(() => {
    if (equipment?.client_id && !reportingPeriods) {
      fetchReportingPeriods(equipment);
    }
  }, [equipment?.client_id, reportingPeriods]);

  useEffect(() => {
    if (equipment && selectedReportingPeriod) {
      fetchPageData(equipment, selectedReportingPeriod);
    }
  }, [equipment, selectedReportingPeriod]);

  useEffect(() => {
    if (!isMetered) {
      if (formValues.is_paused) {
        updateFormValue('work_days_per_quarter', 0);
      } else if (oldWorkDaysValue !== undefined) {
        updateFormValue('work_days_per_quarter', oldWorkDaysValue);
      }
    }
  }, [isMetered, formValues.is_paused, oldWorkDaysValue]);

  /** Render **/
  return (
    <Card data-tour={dataTour}>
      <CardHeader title={t('equipment:equipmentUsage')} icon={<EquipmentUsageIcon />}>
        {currentUsage && (
          <AddButton
            background="accent-1"
            color="white"
            style={{ opacity: isLoading || isUpdatingCurrent || !isEditable ? 0.5 : 1 }}
            label={t('equipment:saveUsage')}
            tip={`${t('equipment:saveChangesTo')} ${getPeriodFromDateString(
              currentUsage.start_reporting_quarter,
              isCanada
            )}`}
            disabled={isLoading || isUpdatingCurrent || !isEditable}
            onClick={() => updateEquipmentUsage(currentUsage.id, formValues)}
          />
        )}
      </CardHeader>
      <CardBody
        gap={!isLoading && !currentUsage ? 'none' : '1.5rem'}
        pad={!isLoading && !currentUsage ? 'none' : undefined}
      >
        {isLoading && (
          <Box pad={{ vertical: 'medium' }} height={pxToRem(277)} fill="horizontal" justify="center" align="center">
            <Spinner size="medium" />
          </Box>
        )}
        {!isLoading && equipment && reportingPeriods && selectedReportingPeriod && (
          <Box row justify="between" align="center" pad={!isLoading && !currentUsage ? 'medium' : undefined}>
            <Box gap="xsmall" justify="center">
              <Box row gap="xsmall">
                <Text size="xlarge" weight={500}>
                  {t('equipment:viewing')}
                </Text>
                <Text size="xlarge" weight={500} color="accent-1">
                  {selectedReportingPeriod &&
                    getPeriodFromDateString(selectedReportingPeriod.start_reporting_quarter, isCanada)}
                </Text>
              </Box>
              <Box>
                <Text size="medium" weight={300}>
                  {dayjs(selectedReportingPeriod?.start_reporting_quarter).format('MMMM DD, YYYY')} -{' '}
                  {dayjs(selectedReportingPeriod?.start_reporting_quarter)
                    .add(2, 'months')
                    .endOf('month')
                    .format('MMMM DD, YYYY')}
                </Text>
              </Box>
            </Box>
            {!isEditable && (
              <Box justify="center" align="center" pad="1rem" gap="xsmall">
                <Text fontFamily="Lato, sans-serif">{disabledMessage}</Text>
              </Box>
            )}
            <ReportingPeriodDropdown
              reportingPeriods={reportingPeriods}
              setSelectedReportingPeriod={setSelectedReportingPeriod}
              selectedReportingPeriod={selectedReportingPeriod}
              previousPeriod={previousPeriod}
              nextPeriod={nextPeriod}
              isCanada={isCanada}
              showNav
            />
          </Box>
        )}
        {!isLoading && (
          <Box gap="xsmall">
            {currentUsage && (
              <Box gap="medium">
                <FormCardSection>
                  <Box direction={isMobile ? 'column' : 'row'} gap="medium">
                    {!isMetered && (
                      <CheckBox
                        id="is_paused"
                        name="is_paused"
                        label={t('equipment:pausedForSeasonality')}
                        checked={formValues.is_paused as boolean}
                        onChange={(e) => handleCheckBox(e.target.checked)}
                        disabled={!isEditable}
                      />
                    )}

                    {userStore.isInternalUser && (
                      <Select
                        id="fuel_pathway_id"
                        label={t('equipment:fuelPathwayCode')}
                        placeholder={!isFetchingPathways ? t('common:choose') : t('common:loading')}
                        value={!isFetchingPathways ? formValues.fuel_pathway_id : ''}
                        setValue={(value) => updateFormValue('fuel_pathway_id', value)}
                        options={[{ label: '\u00A0', value: null }, ...equipmentUsageStore.fuelPathwayOptions]}
                        error={formErrors['fuel_pathway_id']}
                        onSearch={searchPathways}
                        searchPlaceholder={t('equipment:filterByCode')}
                        emptySearchMessage={errorMessages.fuelPathwayNameSearchNoResult}
                        fill="horizontal"
                        disabled={!userStore.isAdminUser || !isEditable || isFetchingPathways}
                      />
                    )}

                    {userStore.isInternalUser && (
                      <Select
                        id="is_book_and_claim_applied"
                        label={t('equipment:bookAndClaim')}
                        value={
                          formValues.is_book_and_claim_applied === null ||
                          formValues.is_book_and_claim_applied === undefined
                            ? null
                            : formValues.is_book_and_claim_applied
                              ? 'Applied'
                              : 'Not Applied'
                        }
                        setValue={(value) =>
                          updateFormValue(
                            'is_book_and_claim_applied',
                            value === 'Applied' ? true : value === 'Not Applied' ? false : value === null ? null : value
                          )
                        }
                        options={[
                          { label: '\u00A0', value: null },
                          { label: t('equipment:applied'), value: 'Applied' },
                          { label: t('equipment:notApplied'), value: 'Not Applied' },
                        ]}
                        error={formErrors['is_book_and_claim_applied']}
                        disabled={!isEditable}
                        fill="horizontal"
                      />
                    )}

                    {!isMetered && (
                      <Input
                        id="battery_capacity_rating_ah"
                        label={t('equipment:batteryCapacityAh')}
                        value={formValues.battery_capacity_rating_ah as string}
                        setValue={(value) => updateFormValue('battery_capacity_rating_ah', value)}
                        onSubmit={() => updateEquipmentUsage(currentUsage.id, formValues)}
                        fill="horizontal"
                        type="number"
                        disabled={!isEditable}
                        required
                        hideOptionalText
                      />
                    )}
                    {!isMetered && (
                      <Input
                        id="voltage"
                        label={t('equipment:voltage')}
                        value={formValues.voltage as string}
                        setValue={(value) => updateFormValue('voltage', value)}
                        onSubmit={() => updateEquipmentUsage(currentUsage.id, formValues)}
                        fill="horizontal"
                        type="number"
                        disabled={!isEditable}
                        required
                        hideOptionalText
                      />
                    )}
                  </Box>
                </FormCardSection>
                <UsagesDataTable
                  formValues={formValues}
                  updateFormValue={updateFormValue}
                  formErrors={formErrors}
                  tableCells={
                    isMetered
                      ? [
                          {
                            label: t('common:totalKwh'),
                            valueKey: 'total_kwh',
                            isEditable,
                            formatNumber: !isEditable,
                            unit: !isEditable ? 'kwh' : undefined,
                          },
                          ...(showFciFields
                            ? ([
                                {
                                  label: t('equipment:percentUptime'),
                                  valueKey: 'percent_uptime',
                                  isEditable,
                                  unit: 'percent',
                                },
                                {
                                  label: t('equipment:maxSiteDeliverabilityKw'),
                                  valueKey: 'max_site_deliverability_kw',
                                  isEditable,
                                },
                              ] satisfies DataTableCell[])
                            : []),
                        ]
                      : [
                          {
                            label: t('equipment:chargeCyclesShift'),
                            valueKey: 'charge_cycles_per_shift',
                            isEditable,
                          },
                          {
                            label: t('equipment:shiftsDay'),
                            valueKey: 'shifts_per_day',
                            isEditable,
                          },
                          {
                            label: t('equipment:workDaysQuarter'),
                            valueKey: 'work_days_per_quarter',
                            isEditable: isEditable && !formValues.is_paused,
                          },
                          {
                            label: t('equipment:chargerEfficiencyRating'),
                            valueKey: 'percent_charger_efficiency_rating',
                            isEditable,
                            unit: 'percent',
                            inputMode: 'numeric',
                          },
                          {
                            label: t('equipment:chargeReturnFactor'),
                            valueKey: 'percent_charge_return_factor',
                            isEditable,
                            unit: 'percent',
                            inputMode: 'numeric',
                          },
                          {
                            label: t('equipment:depthOfDischarge'),
                            valueKey: 'percent_depth_of_discharge',
                            isEditable,
                            unit: 'percent',
                            inputMode: 'numeric',
                          },
                          ...(showFciFields
                            ? ([
                                {
                                  label: t('equipment:percentUptime'),
                                  valueKey: 'percent_uptime',
                                  isEditable,
                                  required: true,
                                  unit: 'percent',
                                },
                                {
                                  label: t('equipment:maxSiteDeliverabilityKw'),
                                  valueKey: 'max_site_deliverability_kw',
                                  isEditable,
                                },
                              ] satisfies DataTableCell[])
                            : []),
                        ]
                  }
                  cellWidth="100%"
                  equipment={equipment}
                  equipmentUsages={[currentUsage]}
                  isCanada={isCanada}
                />
              </Box>
            )}

            {!currentUsage && (
              <Box pad={{ horizontal: '1.5rem', vertical: '2rem' }} background="light-6" justify="center">
                <Text alignSelf="center" size="medium" fontFamily="Lato, sans-serif">
                  {t('equipment:noUsageDataFound')}
                </Text>
              </Box>
            )}
          </Box>
        )}
      </CardBody>
    </Card>
  );
});
